<template>
    <form
        class="w-full self-stretch md:pt-6 justify-between items-end flex flex-col md:flex-row @container"
        @submit.prevent="noSubmit"
    >
        <!-- START SEARCH INPUT -->
        <FavoritesNameSearch />
        <!-- END SEARCH INPUT -->
        <div
            class="md:justify-end justify-start gap-1 md:gap-6 flex md:py-2 mt-2 md:mt-0 w-full"
        >
            <!-- START SHOW ON MAP BUTTON -->
            <FavoritesActionBarButton
                :customClickEvent="true"
                :hideForMobile="true"
                @buttonClicked="openFavsMap"
                @keydown.enter.prevent="openFavsMap"
                class="hidden lg:block"
            >
                <template #icon-left>
                    <i class="fa-regular fa-map fa-sm"></i>
                </template>
                <template #button-text>
                    {{ showOnMap ? 'Hide map' : 'Open Map View' }}
                </template>
            </FavoritesActionBarButton>
            <!-- END SHOW ON MAP BUTTON -->
            <!-- START GROUPING DROPDOWN -->
            <FavoritesActionBarButton>
                <template #icon-left>
                    <div class="w-5 h-5 relative">
                        <div
                            class="w-[15px] h-[4.17px] left-[2.50px] top-[4.17px] absolute rounded-sm border border-slate-800"
                        ></div>

                        <div
                            class="w-[15px] h-[4.17px] left-[2.50px] top-[11.67px] absolute rounded-sm border border-slate-800"
                        ></div>
                    </div>
                </template>
                <template #button-text>
                    {{
                        selectedGroupingOption
                            ? selectedGroupingOption.label
                            : 'Group by'
                    }}
                </template>
                <template #menu-items>
                    <MenuItems
                        class="p-2 bg-white rounded-lg shadow-sm border border-slate-200 justify-start items-start flex flex-col absolute right-0 mt-1 origin-top-right z-50 active:border-0 focus:outline-none"
                    >
                        <MenuItem
                            as="button"
                            v-for="(option, i) in groupingOptions"
                            :tag="`option-${i}`"
                            class="w-full flex-col justify-start items-start flex hover:bg-slate-100 border-2 border-transparent active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                            v-slot="{ active }"
                            @click="handleSelectGroupingOption(option)"
                        >
                            <div
                                class="p-2 rounded justify-start gap-3 flex text-slate-800 text-sm font-normal leading-tight whitespace-nowrap w-full"
                                :class="[active ? 'bg-slate-100' : 'bg-white']"
                            >
                                {{ option.label }}
                            </div>
                        </MenuItem>
                    </MenuItems>
                </template>
            </FavoritesActionBarButton>
            <!-- END GROUPING DROPDOWN -->
            <!-- START SORT DROPDOWN -->
            <FavoritesActionBarButton>
                <template #icon-left>
                    <i class="fa-solid fa-bars-sort fa-sm"></i>
                </template>
                <template #button-text>
                    {{
                        selectedSortOption
                            ? sortOptions.find(
                                  (o) => o.value === selectedSortOption
                              ).name
                            : 'Sort by'
                    }}
                </template>
                <template #menu-items>
                    <MenuItems
                        class="p-2 bg-white rounded-lg shadow-sm border border-slate-200 justify-start items-start flex flex-col absolute right-0 mt-1 origin-top-right z-50 active:border-0 focus:outline-none"
                    >
                        <MenuItem
                            as="button"
                            v-for="(option, i) in sortOptions"
                            v-slot="{ active }"
                            class="w-full flex-col justify-start items-start flex hover:bg-slate-100 border-2 border-transparent active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                            @click="handleSort(option.value)"
                        >
                            <div
                                class="w-full p-2 rounded justify-start gap-3 flex text-slate-800 text-sm font-normal leading-tight whitespace-nowrap"
                                :class="[active ? 'bg-slate-100' : 'bg-white']"
                            >
                                {{ option.name }}
                            </div>
                        </MenuItem>
                    </MenuItems>
                </template>
            </FavoritesActionBarButton>
            <!-- END SORT DROPDOWN -->
        </div>
    </form>
</template>

<script>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import FavoritesActionBarButton from '@/components/hotels/favorites/favorites-action-bar/favorites-action-bar-button/favorites-action-bar-button.vue';
import FavoritesNameSearch from '@/components/hotels/favorites/favorites-name-search/favorites-name-search.vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';

export default {
    components: {
        FavoritesActionBarButton,
        FavoritesNameSearch,
        Menu,
        MenuButton,
        MenuItems,
        MenuItem,
    },
    setup() {
        const hotelsStore = useHotelsStore();
        const { usersFavs, showOnMap } = storeToRefs(hotelsStore);
        const {
            setShowOnMap,
            setGroupFavsBy,
            setSort,
            getUsersFavHotels,
            setMapType,
        } = hotelsStore;
        const selectedGroupingOption = ref(null);
        const selectedSortOption = computed(() => hotelsStore.sort);
        const groupingOptions = [
            { label: 'No grouping', value: null },
            { label: 'Reward program', value: 'group' },
            { label: 'Country', value: 'country' },
        ];

        const handleSelectGroupingOption = (val) => {
            selectedGroupingOption.value = val;
            setGroupFavsBy(val.value);
            getUsersFavHotels();
        };

        const handleSort = (input) => {
            setSort(input, 'getFavorites');
        };

        const sortOptions = ref([
            { name: 'Default Sort', value: null },
            {
                name: 'Name A to Z',
                value: 'name',
            },
            {
                name: 'Name Z to A',
                value: '-name',
            },
            {
                name: 'Date Added - Oldest First',
                value: 'favorited_at',
            },
            {
                name: 'Date Added - Newest First',
                value: '-favorited_at',
            },
        ]);

        const noSubmit = () => {};

        const openFavsMap = () => {
            const val = !showOnMap.value;
            setShowOnMap(val);
            if (val) {
                setMapType('favorites');
            } else {
                setMapType('search');
            }
        };

        return {
            groupingOptions,
            handleSelectGroupingOption,
            handleSort,
            noSubmit,
            openFavsMap,
            selectedGroupingOption,
            selectedSortOption,
            showOnMap,
            sortOptions,
            usersFavs,
        };
    },
};
</script>
